import { matchPath } from 'react-router'
import { defineMessages } from 'react-intl'
import qs from 'qs'
import { getCurrentPath } from 'src/helpers/browser'
import { NEW_HOME, RENTAL_PROPERTY, RENT_BUY } from 'src/constants/gems'

const MARKET_TAB = 'MARKET_TAB'
const SOLO_BUYER = 'SOLO_BUYER'

export const TAB_NAMES = {
  ADD_HOME: 'ADD_HOME',
  BUYER: 'BUYER',
  FAVORITE: 'FAVORITE',
  HOMES: 'HOMES',
  MARKETS: 'MARKETS',
  SAVED: 'SAVED',
  SEARCH: 'SEARCH',
}

export const YOUR_HOME_DROPDOWN_TAB_NAMES = {
  ADD_HOME: 'addHome',
}

export const FIND_A_HOME_DROPDOWN_TAB_NAMES = {
  BUYING_REPORT: 'buy',
  DIVIDER: 'divider',
  EXPLORE_MARKETS: 'explore',
  FAVORITE_LISTINGS: 'favorite',
  SAVED_SEARCHES: 'saved',
  SEARCH_HOMES: 'search',
}

export const mapActiveTabToActiveDropdownItem = (activeTab, selectedHomeId) => {
  if (!activeTab) return

  switch (activeTab) {
    case TAB_NAMES.BUYER:
      return FIND_A_HOME_DROPDOWN_TAB_NAMES.BUYING_REPORT
    case TAB_NAMES.HOMES:
      return selectedHomeId
    case TAB_NAMES.MARKETS:
      return FIND_A_HOME_DROPDOWN_TAB_NAMES.EXPLORE_MARKETS
    case TAB_NAMES.ADD_HOME:
      return YOUR_HOME_DROPDOWN_TAB_NAMES.ADD_HOME
    case TAB_NAMES.SEARCH:
      return FIND_A_HOME_DROPDOWN_TAB_NAMES.SEARCH_HOMES
    case TAB_NAMES.FAVORITE:
      return FIND_A_HOME_DROPDOWN_TAB_NAMES.FAVORITE_LISTINGS
    case TAB_NAMES.SAVED:
      return FIND_A_HOME_DROPDOWN_TAB_NAMES.SAVED_SEARCHES
    default:
      console.error('Invalid tab provided')
      break
  }
}

export const FROM = {
  NEW_HOME,
  RENTAL_PROPERTY,
  RENT_BUY,
  MARKET_TAB,
  SOLO_BUYER,
}

export const EXPLORE_MARKET_TABS = {
  POPULAR_MARKETS: 'score',
  BIGGER_HOUSE: 'sqft',
  CHEAPER_BUYS: 'price',
  FASTEST_MOVE: 'timeline',
}

export const PATH_HELPERS = {
  buy: {
    matchPath: '/reports/:clientId([-0-9A-Fa-f]{36})/buy',
    regex: /^\/reports\/([-0-9A-Fa-f]{36}|initialize)\/buy/,
    buildPath: (clientId, destination) =>
      clientId ? `/reports/${clientId}/buy${destination || ''}` : '/',
  },
  home: {
    matchPath:
      '/reports/:clientId([-0-9A-Fa-f]{36})/home/:homeId([0-9A-Fa-f]{24})',
    regex: /^\/reports\/([-0-9A-Fa-f]{36})\/home\/([0-9A-Fa-f]{24}|new)/,
    buildPath: (clientId, homeId) =>
      clientId && homeId ? `/reports/${clientId}/home/${homeId}` : '/',
  },
  homeSearch: {
    regex: /^\/home-search\/([-0-9A-Fa-f]{36})/,
    buildPath: customerProfileId =>
      customerProfileId ? `/home-search/${customerProfileId}` : '/',
  },
  landingPage: {
    buildPath: () => `/homebot-for-chrome`,
  },
  markets: {
    matchPath: '/reports/:clientId([-0-9A-Fa-f]{36})/markets',
    regex: /^\/reports\/([-0-9A-Fa-f]{36}|initialize)\/markets/,
    buildPath: (clientId, marketPath) =>
      clientId ? `/reports/${clientId}/markets${marketPath || ''}` : '/',
  },
  mlsDisclaimers: {
    buildPath: () => `/mls-disclaimers`,
  },
  report: {
    matchPath:
      '/reports/:clientId([-0-9A-Fa-f]{36})/:routeKey(home|buy)/:reportResourceId([-0-9A-Fa-f]{24,36})?',
  },
  signup: {
    buildPath: redirectUrl =>
      redirectUrl ? `/sign-up?redirectUrl=${redirectUrl}` : `/sign-up`,
  },
  tuneYourValue: {
    matchPath:
      '/reports/:clientId([-0-9A-Fa-f]{36})/home/:homeId([0-9A-Fa-f]{24})/tune-your-value',
    regex: /^\/reports\/([-0-9A-Fa-f]{36})\/home\/([0-9A-Fa-f]{24}|new)/,
    buildPath: (clientId, homeId) =>
      clientId && homeId
        ? `/reports/${clientId}/home/${homeId}/tune-your-value`
        : '/',
  },
  zip: {
    matchPath:
      '/reports/:clientId([-0-9A-Fa-f]{36})/markets/:zipcode([0-9]{5})',
    regex: /^\/reports\/([-0-9A-Fa-f]{36}|initialize)\/markets\/([0-9]{5})/,
    buildPath: (clientId, zipcode) =>
      clientId ? `/reports/${clientId}/markets/${zipcode || ''}` : '/',
  },
  listing: {
    matchPath:
      '/home-search/:customerProfileId([-0-9A-Fa-f]{36})/listing/:listingId([0-9A-Fa-f]{24})',
    regex: /^\/home-search\/([-0-9A-Fa-f]{36})\/listing\/([-0-9A-Fa-f]{24})/,
    buildPath: (customerProfileId, listingId) =>
      customerProfileId
        ? `/home-search/${customerProfileId}/listing/${listingId}`
        : '/',
  },
  listings: {
    matchPath: '/home-search/:customerProfileId([-0-9A-Fa-f]{36})/listings/',
    regex: /^\/home-search\/([-0-9A-Fa-f]{36})\/listings/,
    buildPath: (customerProfileId, level, name, additionalFilters) => {
      let additional = qs.stringify(additionalFilters)
      additional = additional ? `&${additional}` : ''

      const url = customerProfileId
        ? `/home-search/${customerProfileId}/listings/?${level}=${name}${additional}`
        : '/'
      return url
    },
  },
  // BEGIN TODO::NR-UI - Remove old routes when NR UI deprecated //
  search: {
    buildPath: customerId =>
      `${process.env.REACT_APP_NESTREADY_UI_URL}/hb/${customerId}`,
  },
  savedSearches: {
    buildPath: customerId =>
      `${process.env.REACT_APP_NESTREADY_UI_URL}/hb/${customerId}/saved-searches`,
  },
  favoriteListings: {
    buildPath: customerId =>
      `${process.env.REACT_APP_NESTREADY_UI_URL}/hb/${customerId}/favorite-listings`,
  },
  hbListingIndexPage: {
    buildPath: customerId =>
      `${process.env.REACT_APP_NESTREADY_UI_URL}/hb/${customerId}`,
  },
  hbListingSearchFilteredByZip: {
    buildPath: (customerId, zip) =>
      `${process.env.REACT_APP_NESTREADY_UI_URL}/hb/${customerId}/dashboard?postal_code=${zip}`,
  },
  hbListingShowPage: {
    buildPath: (customerId, listingId, searchParams) => {
      let url = `${process.env.REACT_APP_NESTREADY_UI_URL}/hb/${customerId}/listing/${listingId}`
      if (searchParams) {
        url = `${url}?${qs.stringify(searchParams)}`
      }
      return url
    },
  },
  // END TODO::NR-UI - Remove old routes when NR UI deprecated //
  prodChromebotWebStore: {
    buildPath: () =>
      'https://chrome.google.com/webstore/detail/homebot-for-chrome/oigjeliojmhdahlmjhackoknjmohedfi',
  },
  purl: {
    buildPath: customerProfileId =>
      `${process.env.REACT_APP_PURL_URL}?id=${customerProfileId}`, // Beats me, but this is the only url ENV var that has a backslash already in it
  },
  listingsSearchRedirect: {
    buildPath: () => '/listings/search',
    buildSearch: args => {
      const query = qs.stringify(args)

      return `?${query}`
    },
  },
}

// NEW SEARCH ROUTES
export const SEARCH_PATH_HELPERS = {
  search: {
    regex: /^\/home-search\/([-0-9A-Fa-f]{36})/,
    buildPath: customerProfileId =>
      customerProfileId ? `/home-search/${customerProfileId}` : '/',
  },
  savedSearches: {
    regex: /^\/home-search\/([-0-9A-Fa-f]{36})\/saved-searches/,
    buildPath: customerProfileId =>
      customerProfileId
        ? `/home-search/${customerProfileId}/saved-searches`
        : '/',
  },
  favoriteListings: {
    buildPath: customerProfileId =>
      customerProfileId
        ? `/home-search/${customerProfileId}/favorite-listings`
        : '/',
  },
}

const OVERLAY_PANEL_TITLES = defineMessages({
  airDna: {
    id: 'Homeowners.overlay.airDna.learnMore.title',
    defaultMessage: 'Airbnb rental potential',
  },
  avmDiffWarning: {
    id: 'Homeowners.overlay.avmDiffWarning.title',
    defaultMessage: 'Your home value',
  },
  avmHotMarketWarning: {
    id: 'Homeowners.overlay.avmHotMarketWarning.title',
    defaultMessage: 'Your home value',
  },
  cashout: {
    id: 'Homeowners.overlay.cashout.title',
    defaultMessage: 'Cash Out',
  },
  emailUpdates: {
    id: 'Homeowners.overlay.emailUpdates.title',
    defaultMessage: 'Email updates',
  },
  equitySmartSearch: {
    id: 'Homeowners.overlay.equitySmartSearchCalculation.title',
    defaultMessage: 'How is this calculated?',
  },
  instantOffer: {
    id: 'Homeowners.overlay.instantOffer.title',
    defaultMessage: 'Instant Offer',
  },
  marketSpotlight: {
    id: 'Homeowners.overlay.marketSpotlight.title',
    defaultMessage: 'Your Market',
  },
  mortgageCoach: {
    id: 'Homeowners.overlay.mortgageCoach.title',
    defaultMessage: 'Mortgage Coach',
  },
  mortgageRelief: {
    id: 'Homeowners.overlay.mortgageRelief.title',
    defaultMessage: 'Mortgage Payment Options',
  },
  nonDisclosureWarning: {
    id: 'Homeowners.overlay.nonDisclosureWarning.title',
    defaultMessage: 'Your home value',
  },
  yourInsurance: {
    id: 'Homeowners.overlay.homeInsurance.yourInsurance.title',
    defaultMessage: 'Your Insurance',
  },
  moreDetails: {
    id: 'Homeowners.overlay.homeInsurance.moreDetails.title',
    defaultMessage: "Homeowner's Insurance",
  },
  preferences: {
    id: 'Homeowners.overlay.preferences.title',
    defaultMessage: 'Manage Preferences',
  },
  principalPayment: {
    id: 'Homeowners.overlay.principal.title',
    defaultMessage: 'Learn More',
  },
  purchasing: {
    id: 'Homeowners.overlay.purchasing.title',
    defaultMessage: 'Purchasing Power',
  },
  refinance: {
    id: 'Homeowners.overlay.refinance.title',
    defaultMessage: 'Refi Options',
  },
  refiRateRequest: {
    id: 'Homeowners.overlay.refinance.getRate.title',
    defaultMessage: 'Get Your Rate',
  },
  reverseMortgage: {
    id: 'Homeowners.overlay.reverseMortgage.title',
    defaultMessage: 'Reverse Mortgage',
  },
  shareHomebot: {
    id: 'Homeowners.overlay.shareHomebot.title',
    defaultMessage: 'Share Homebot',
  },
  shouldYouSell: {
    id: 'Homeowners.overlay.shouldYouSell.title',
    defaultMessage: 'Should you sell?',
  },
  textUpdates: {
    id: 'Homeowners.overlay.textUpdates.title',
    defaultMessage: 'Text Updates',
  },
  tuneYourValue: {
    id: 'Homeowners.overlay.tyv.title',
    defaultMessage: 'Tune Your Value',
  },
  unpaidBalance: {
    id: 'Homeowners.overlay.unpaidBalance.title',
    defaultMessage: 'Update your loan balance',
  },
})

export const OVERLAY_HELPERS = {
  airDnaLearnMore: {
    overlay: 'air-dna',
    titleMessage: OVERLAY_PANEL_TITLES.airDna,
    matchPath: [
      `${PATH_HELPERS.home.matchPath}/air-dna/learn-more`,
      `${PATH_HELPERS.buy.matchPath}/air-dna/learn-more`,
    ],
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      const buyRoutes = getCurrentPath().match(PATH_HELPERS.buy.regex)
      const matchedRoutes = homeRoutes || buyRoutes
      return matchedRoutes ? matchedRoutes[0] : null
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      const buyRoutes = getCurrentPath().match(PATH_HELPERS.buy.regex)
      const matchedRoutes = homeRoutes || buyRoutes
      return matchedRoutes
        ? `${matchedRoutes[0]}/air-dna/learn-more`
        : '/not-found'
    },
  },
  avmDiffWarning: {
    overlay: 'your-home-value',
    titleMessage: OVERLAY_PANEL_TITLES.avmDiffWarning,
    matchPath: `${PATH_HELPERS.home.matchPath}/your-home-value`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? `${homeRoutes[0]}/your-home-value` : '/not-found'
    },
  },
  avmHotMarketWarning: {
    overlay: 'high-demand-home-value',
    titleMessage: OVERLAY_PANEL_TITLES.avmHotMarketWarning,
    matchPath: `${PATH_HELPERS.home.matchPath}/high-demand-home-value`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes
        ? `${homeRoutes[0]}/high-demand-home-value`
        : '/not-found'
    },
  },
  cashout: {
    overlay: 'cashout',
    titleMessage: OVERLAY_PANEL_TITLES.cashout,
    matchPath: `${PATH_HELPERS.home.matchPath}/cashout/:gemKey([^/]+?)`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : null
    },
    buildPath: gemKey => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes && gemKey
        ? `${homeRoutes[0]}/cashout/${gemKey.toLowerCase()}`
        : '/not-found'
    },
  },
  emailUpdates: {
    overlay: 'email-updates',
    titleMessage: OVERLAY_PANEL_TITLES.emailUpdates,
    matchPath: [
      `${PATH_HELPERS.home.matchPath}/email-updates`,
      `${PATH_HELPERS.buy.matchPath}/email-updates`,
    ],
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      const buyRoutes = getCurrentPath().match(PATH_HELPERS.buy.regex)
      const matchedRoutes = homeRoutes || buyRoutes
      return matchedRoutes ? matchedRoutes[0] : null
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      const buyRoutes = getCurrentPath().match(PATH_HELPERS.buy.regex)
      const matchedRoutes = homeRoutes || buyRoutes
      return matchedRoutes ? `${matchedRoutes[0]}/email-updates` : '/not-found'
    },
  },
  equitySmartSearch: {
    overlay: 'how-is-this-calculated',
    titleMessage: OVERLAY_PANEL_TITLES.equitySmartSearch,
    matchPath: `${PATH_HELPERS.home.matchPath}/how-is-this-calculated`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes
        ? `${homeRoutes[0]}/how-is-this-calculated`
        : '/not-found'
    },
  },
  homeInsurance: {
    overlay: 'home-insurance',
    titleMessage: OVERLAY_PANEL_TITLES.homeInsurance,
    matchPath: `${PATH_HELPERS.home.matchPath}/home-insurance`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: path => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes
        ? `${homeRoutes[0]}/home-insurance/${path}`
        : '/not-found'
    },
  },
  instantOffer: {
    overlay: 'instant-offer',
    titleMessage: OVERLAY_PANEL_TITLES.instantOffer,
    matchPath: `${PATH_HELPERS.home.matchPath}/instant-offer`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? `${homeRoutes[0]}/instant-offer` : '/not-found'
    },
  },
  marketSpotlight: {
    overlay: 'your-market',
    titleMessage: OVERLAY_PANEL_TITLES.marketSpotlight,
    matchPath: `${PATH_HELPERS.home.matchPath}/your-market`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? `${homeRoutes[0]}/your-market` : '/not-found'
    },
  },
  mortgageCoach: {
    overlay: 'mortgage-coach',
    titleMessage: OVERLAY_PANEL_TITLES.mortgageCoach,
    matchPath: `${PATH_HELPERS.home.matchPath}/refi-options/:refiKey([^/]+?)/mortgage-coach`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(
        `${PATH_HELPERS.home.regex}/refi-options/([^/]+?)`
      )
      return homeRoutes ? homeRoutes[0] : null
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(
        `${PATH_HELPERS.home.regex}/refi-options/([^/]+?)`
      )
      return homeRoutes ? `${homeRoutes[0]}/mortgage-coach` : '/not-found'
    },
  },
  mortgageRelief: {
    overlay: 'mortgage-relief',
    titleMessage: OVERLAY_PANEL_TITLES.mortgageRelief,
    matchPath: `${PATH_HELPERS.home.matchPath}/mortgage-relief`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? `${homeRoutes[0]}/mortgage-relief` : '/not-found'
    },
  },
  nonDisclosureWarning: {
    overlay: 'non-disclosure-state',
    titleMessage: OVERLAY_PANEL_TITLES.nonDisclosureWarning,
    matchPath: `${PATH_HELPERS.home.matchPath}/non-disclosure-state`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? `${homeRoutes[0]}/non-disclosure-state` : '/not-found'
    },
  },
  preferences: {
    overlay: 'preferences',
    titleMessage: OVERLAY_PANEL_TITLES.preferences,
    matchPath: [
      `${PATH_HELPERS.home.matchPath}/preferences`,
      `${PATH_HELPERS.buy.matchPath}/preferences`,
    ],
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      const buyRoutes = getCurrentPath().match(PATH_HELPERS.buy.regex)
      const listingsRoutes = getCurrentPath().match(PATH_HELPERS.listings.regex)
      if (homeRoutes) {
        return homeRoutes[0]
      }
      if (buyRoutes) {
        return buyRoutes[0]
      }
      if (listingsRoutes) {
        return listingsRoutes[0]
      }
      return null
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      const buyRoutes = getCurrentPath().match(PATH_HELPERS.buy.regex)
      const listingsRoutes = getCurrentPath().match(PATH_HELPERS.listings.regex)
      if (homeRoutes) {
        return `${homeRoutes[0]}/preferences`
      }
      if (buyRoutes) {
        return `${buyRoutes[0]}/preferences`
      }
      if (listingsRoutes) {
        return `${listingsRoutes[0]}/preferences`
      }
      return '/not-found'
    },
  },
  principalPaymentLearnMore: {
    overlay: 'principal-payment',
    titleMessage: OVERLAY_PANEL_TITLES.principalPayment,
    matchPath: [
      `${PATH_HELPERS.home.matchPath}/principal-payment/learn-more`,
      `${PATH_HELPERS.buy.matchPath}/principal-payment/learn-more`,
    ],
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      const buyRoutes = getCurrentPath().match(PATH_HELPERS.buy.regex)
      const matchedRoutes = homeRoutes || buyRoutes
      return matchedRoutes ? matchedRoutes[0] : null
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      const buyRoutes = getCurrentPath().match(PATH_HELPERS.buy.regex)
      const matchedRoutes = homeRoutes || buyRoutes
      return matchedRoutes
        ? `${matchedRoutes[0]}/principal-payment/learn-more`
        : '/not-found'
    },
  },
  purchasing: {
    overlay: 'purchasing',
    titleMessage: OVERLAY_PANEL_TITLES.purchasing,
    matchPath: `${PATH_HELPERS.home.matchPath}/purchasing/:gemKey([^/]+?)`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : null
    },
    buildPath: gemKey => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes && gemKey
        ? `${homeRoutes[0]}/purchasing/${gemKey.toLowerCase()}`
        : '/not-found'
    },
  },
  refinance: {
    overlay: 'refi-options',
    titleMessage: OVERLAY_PANEL_TITLES.refinance,
    matchPath: `${PATH_HELPERS.home.matchPath}/refi-options/:refiKey([^/]+?)`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : null
    },
    buildPath: refiKey => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes && refiKey
        ? `${homeRoutes[0]}/refi-options/${refiKey.toLowerCase()}`
        : '/not-found'
    },
  },
  refiRateRequest: {
    overlay: 'get-rate',
    titleMessage: OVERLAY_PANEL_TITLES.refiRateRequest,
    matchPath: `${PATH_HELPERS.home.matchPath}/refi-options/:refiKey([^/]+?)/get-rate`,
    getOrigin: () => {
      const refiRoutes =
        matchPath(getCurrentPath(), {
          path: `${PATH_HELPERS.home.matchPath}/refi-options/:refiKey([^/]+?)`,
        }) || {}
      return refiRoutes.url || null
    },
    buildPath: () => {
      const refiRoutes =
        matchPath(getCurrentPath(), {
          path: `${PATH_HELPERS.home.matchPath}/refi-options/:refiKey([^/]+?)`,
        }) || {}
      return refiRoutes.url ? `${refiRoutes.url}/get-rate` : '/not-found'
    },
  },
  shareHomebot: {
    overlay: 'share-homebot',
    titleMessage: OVERLAY_PANEL_TITLES.shareHomebot,
    matchPath: `${PATH_HELPERS.home.matchPath}/share-homebot`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? `${homeRoutes[0]}/share-homebot` : '/not-found'
    },
  },
  shouldYouSell: {
    overlay: 'should-you-sell',
    titleMessage: OVERLAY_PANEL_TITLES.shouldYouSell,
    matchPath: `${PATH_HELPERS.home.matchPath}/should-you-sell`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? `${homeRoutes[0]}/should-you-sell` : '/not-found'
    },
  },
  textUpdates: {
    overlay: 'text-updates',
    titleMessage: OVERLAY_PANEL_TITLES.textUpdates,
    matchPath: [
      `${PATH_HELPERS.home.matchPath}/text-updates`,
      `${PATH_HELPERS.buy.matchPath}/text-updates`,
    ],
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      const buyRoutes = getCurrentPath().match(PATH_HELPERS.buy.regex)
      const matchedRoutes = homeRoutes || buyRoutes
      return matchedRoutes ? matchedRoutes[0] : null
    },
    buildPath: key => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      const buyRoutes = getCurrentPath().match(PATH_HELPERS.buy.regex)
      const matchedRoutes = homeRoutes || buyRoutes
      return matchedRoutes
        ? `${matchedRoutes[0]}/text-updates${key ? `/${key}` : ''}`
        : '/not-found'
    },
  },
  tuneYourValue: {
    overlay: 'tune-your-value',
    titleMessage: OVERLAY_PANEL_TITLES.tuneYourValue,
    matchPath: `${PATH_HELPERS.home.matchPath}/tune-your-value`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? `${homeRoutes[0]}/tune-your-value` : '/not-found'
    },
  },
  unpaidBalance: {
    overlay: 'unpaid-balance',
    titleMessage: OVERLAY_PANEL_TITLES.unpaidBalance,
    matchPath: `${PATH_HELPERS.home.matchPath}/unpaid-balance`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? `${homeRoutes[0]}/unpaid-balance` : '/not-found'
    },
  },
  reverseMortgage: {
    overlay: 'reverse-mortgage',
    titleMessage: OVERLAY_PANEL_TITLES.reverseMortgage,
    matchPath: `${PATH_HELPERS.home.matchPath}/reverse-mortgage`,
    getOrigin: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? homeRoutes[0] : '/'
    },
    buildPath: () => {
      const homeRoutes = getCurrentPath().match(PATH_HELPERS.home.regex)
      return homeRoutes ? `${homeRoutes[0]}/reverse-mortgage` : '/not-found'
    },
  },
  getTitle: (key, intl) => {
    const title = OVERLAY_PANEL_TITLES[key]
    if (!title) {
      return
    }

    if (typeof intl === 'function') {
      return intl(title)
    }
    if (intl && intl.formatMessage) {
      return intl.formatMessage(title)
    }
    return title.defaultMessage
  },
  listingDetailsPhotos: {
    overlay: 'listingDetailsPhotos',
    getOrigin: () => {
      const listingsRoutes = getCurrentPath().match(PATH_HELPERS.listing.regex)

      if (listingsRoutes) {
        return listingsRoutes[0]
      }
      return null
    },
    buildPath: () => {
      const listingRoute = getCurrentPath().match(PATH_HELPERS.listing.regex)

      if (listingRoute) {
        return `${listingRoute[0]}/photos`
      }

      return null
    },
  },
  listingDetailsMap: {
    overlay: 'listingDetailsMap',
    getOrigin: () => {
      const listingsRoutes = getCurrentPath().match(PATH_HELPERS.listing.regex)

      if (listingsRoutes) {
        return listingsRoutes[0]
      }
      return null
    },
    buildPath: () => {
      const listingRoute = getCurrentPath().match(PATH_HELPERS.listing.regex)
      return `${listingRoute[0]}/map`
    },
  },
}

export const getClientIdFromPath = () => {
  const pathname = getCurrentPath()
  const clientMatch = matchPath(pathname, {
    path: '/reports/:clientId([-0-9A-Fa-f]{36})',
  })

  return clientMatch ? clientMatch.params.clientId : null
}

export const getHomeIdFromPath = () => {
  const pathname = getCurrentPath()
  const homeMatch = matchPath(pathname, {
    path: '/reports/:clientId?/home/:homeId([0-9A-Fa-f]{24})',
  })
  return homeMatch ? homeMatch.params.homeId : null
}

export const EMAIL_PROVIDERS = {
  GMAIL: {
    title: 'Gmail',
    url: 'https://mail.google.com/mail/u/0/',
  },
  OUTLOOK: {
    title: 'Outlook',
    url: 'https://outlook.live.com/mail/0/inbox',
  },
  YAHOO: {
    title: 'Yahoo',
    url: 'https://mail.yahoo.com',
  },
}

export const TOS_LINK = 'https://homebot.ai/terms-of-service'
export const PRIVACY_LINK = 'https://homebot.ai/privacy-policy'
